import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RegistrationStepResponse, RegistrationStatus, RegistrationStep } from '../../core/services/registration-step.service';
import { ProfessionalsService } from '../../core/services/professionals.service';
import { QuestionnaireService } from '../../core/services/questionnaire.service';
import { environment } from '../../../environments/environment';
import { catchError, of, forkJoin, map } from 'rxjs';
/**
 * Interfaccia per la gestione degli step nel Wizard Frontend
 */
export interface ProfessionalsWizardStep {
  id: number;
  label: string;
  routerLink: string;
  completed: boolean;
  active: boolean;
  icon?: string;
  command?: () => void;
  disabled?: boolean;
  tooltipMessage?: string;
  hidden?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ProfessionalsWizardService {
  private http = inject(HttpClient);
  private professionalsService = inject(ProfessionalsService);
  private questionnaireService = inject(QuestionnaireService);
  private readonly API_BASE_URL = 'api/v1/professionals';

  // Mapping tra lo Step del Backend e l'ID del Wizard Frontend
  private readonly stepMap: Record<string, number> = {
    [RegistrationStep.PORTAL_ACCESS]: 0,
    [RegistrationStep.DATA_INPUT]: 1,
    [RegistrationStep.VERIFICATION]: 2,
    [RegistrationStep.CONTRACT_SENT]: 3
  };

  steps = signal<ProfessionalsWizardStep[]>([
    { id: 0, label: 'Dati Generali', routerLink: '/axa/professionals-general-data', icon: 'pi pi-user', completed: false, active: true, disabled: false },
    { id: 1, label: 'Dati Specifici', routerLink: '/axa/professionals-specific-data', icon: 'pi pi-building', completed: false, active: false, disabled: false },
    { id: 3, label: 'Collaboratori Professionisti e Impiegati Amministrativi', routerLink: '/axa/professionals-staff-collaborators', icon: 'pi pi-users', completed: false, active: false, disabled: false },
    { id: 2, label: 'Questionari e Allegati', routerLink: '/axa/professionals-questionnaire-attachments', icon: 'pi pi-file', completed: false, active: false, disabled: false },
  ]);

  currentStep = signal<number>(0);
  
  // Current registration step from backend (PORTAL_ACCESS, DATA_INPUT, VERIFICATION, etc.)
  currentRegistrationStep = signal<RegistrationStep | null>(null);
  
  // Current registration status from backend (PENDING, USER_INPUT, COMPLETED, etc.)
  currentRegistrationStatus = signal<RegistrationStatus | null>(null);

  private skipUnsavedChangesWarning = signal<boolean>(false);

  allowNextNavigationWithoutWarning(): void {
    this.skipUnsavedChangesWarning.set(true);
  }

  consumeNavigationWithoutWarning(): boolean {
    const shouldSkipWarning = this.skipUnsavedChangesWarning();
    this.skipUnsavedChangesWarning.set(false);
    return shouldSkipWarning;
  }

  // Network body shop onboarding mode — set when BO creates a new NET- body shop
  isNetworkOnboarding = signal<boolean>(false);
  networkOnboardingNetworkId = signal<string>('');
  networkOnboardingBodyShopId = signal<string>('');
  // Operational Location onboarding mode – set when a bodyshop registers a new sede operativa
  isOperationalLocationOnboarding = signal<boolean>(false);
  operationalLocationBodyShopId = signal<string>('');
  /** Status of the NK body shop currently open.
   *  null  = still loading (HTTP pending)
   *  ''    = new bodyshop, not yet activated
   *  'ACTIVE' | 'SUSPENDED' | 'IN_PROGRESS' | ... = known status from BE */
  networkBodyShopStatus = signal<string | null>(null);

  /** True when the doctor (medico legale) professional is a public employee.
   *  Used to conditionally adapt the banking data section in general-data. */
  isPublicEmployeeDoctor = signal<boolean>(false);

  /**
  * Initialize wizard for network body shop onboarding.
  * - Hides "Dati Generali" (step 0) and "Impiegati amministrativi" (step 3)
   * - Pre-populates the general data cache so specific-data detects NK_BODYSHOP type
   * - Marks step 0 as completed so areRequiredStepsCompleted passes
   */
  /**
   * Synchronously prepares wizard steps for NK onboarding so the navbar can
   * render the correct steps immediately (before the async HTTP call completes).
   * Call this BEFORE getNetworkBodyShopById to avoid the brief wrong-state window.
   */
  prepareNKStepVisibility(bodyShopId: string): void {
    this.isNetworkOnboarding.set(true);
    this.networkOnboardingBodyShopId.set(bodyShopId);
    this.networkBodyShopStatus.set(null); // status pending — HTTP call in the routed component will resolve it
    // IMPORTANT: spread-with-override must include hidden:false for steps 1 and 2.
    // A previous NETWORK flow calls hideStep(1) which saves hidden:true to sessionStorage.
    // Without explicit hidden:false here, step 1 would remain invisible in the navbar.
    // completed:false is also reset explicitly to avoid stale state from a previous wizard session
    // (the service is a singleton and in-memory state persists across SPA navigation).
    this.steps.update(steps => steps.map(s => {
      if (s.id === 1) return { ...s, routerLink: `/axa/professionals-specific-data/${bodyShopId}`, active: true, hidden: false, completed: false };
      if (s.id === 2) return { ...s, label: 'Allegati', routerLink: `/axa/professionals-questionnaire-attachments/${bodyShopId}`, active: false, hidden: false, completed: false };
      return { ...s, active: false };
    }));
    this.hideStep(0, true);
    this.hideStep(3, true);
  }

  initNetworkOnboarding(bodyShopId: string, networkId: string, bodyShopSubType: string = 'STANDARD', status: string = ''): void {
    this.isNetworkOnboarding.set(true);
    this.networkOnboardingNetworkId.set(networkId);
    this.networkOnboardingBodyShopId.set(bodyShopId);
    this.networkBodyShopStatus.set(status);

    // Pre-populate cache so specific-data component loads BodyshopFormComponent for NK_BODYSHOP
    this.generalDataCache.set({ professionalType: 'NK_BODYSHOP', professionalSubType: bodyShopSubType });
    this.markStepCompleted(0, true); // step 0 not required in this flow

    // Update routerLinks for steps 1 and 2 to include the NK bodyShopId so
    // the navbar wizard menu navigates to the /:id routes (backoffice_professional roles)
    // instead of the no-id routes (professional/collaborator roles only).
    this.steps.update(steps => steps.map(s => {
      if (s.id === 1) return { ...s, routerLink: `/axa/professionals-specific-data/${bodyShopId}`, hidden: false, completed: false };
      if (s.id === 2) return { ...s, label: 'Allegati', routerLink: `/axa/professionals-questionnaire-attachments/${bodyShopId}`, hidden: false, completed: false };
      return s;
    }));

    this.hideStep(0, true);
    this.hideStep(3, true);
    this.setStep(1);
    this.saveState();
  }

  /** Reset network onboarding mode (call when leaving the wizard) */
  clearNetworkOnboarding(): void {
    this.isNetworkOnboarding.set(false);
    this.networkOnboardingNetworkId.set('');
    this.networkOnboardingBodyShopId.set('');
    this.networkBodyShopStatus.set(null);
    // Restore hidden steps
    this.hideStep(0, false);
    this.hideStep(3, false);
    this.saveState();
  }

  /**
   * Synchronously prepares wizard steps for Operational Location onboarding.
   * Steps 0 (Dati Generali), 1 (Dati Specifici) and 3 (Impiegati Amministrativi e
   * Collaboratori) are visible – step 2 (Questionari e Allegati) is hidden.
   * On step 0 only the "Dati Istituzionali Generali" subsection is shown (handled by the component).
   * Per D6, the unified staff section is shown and required for a normal bodyshop
   * operational location (it is NOT auto-completed here).
   */
  prepareOperationalLocationStepVisibility(bodyShopId: string): void {
    this.isOperationalLocationOnboarding.set(true);
    this.operationalLocationBodyShopId.set(bodyShopId);
    const activeStepId = this.currentStep();
    this.steps.update(steps => steps.map(s => {
      if (s.id === 0) return { ...s, routerLink: '/axa/professionals-general-data', active: activeStepId === 0, hidden: false };
      if (s.id === 1) return { ...s, routerLink: '/axa/professionals-specific-data', active: activeStepId === 1, hidden: false, completed: false };
      if (s.id === 3) return { ...s, active: activeStepId === 3, hidden: false };
      return { ...s, active: false };
    }));
    this.hideStep(2, true);
    this.markStepCompleted(2, true);
    this.saveState();
  }

  /** Reset operational location onboarding mode (call when leaving the wizard) */
  clearOperationalLocationOnboarding(): void {
    this.isOperationalLocationOnboarding.set(false);
    this.operationalLocationBodyShopId.set('');
    this.hideStep(0, false);
    this.hideStep(2, false);
    this.hideStep(3, false);
    this.saveState();
  }

  /**
   * Full reset to initial default state — use when switching from NK onboarding
   * to a classic professionals wizard to avoid sessionStorage contamination.
   * Clears sessionStorage so loadState() starts from scratch.
   */
  resetToDefault(): void {
    this.isNetworkOnboarding.set(false);
    this.networkOnboardingNetworkId.set('');
    this.networkOnboardingBodyShopId.set('');
    this.networkBodyShopStatus.set(null);
    this.isOperationalLocationOnboarding.set(false);
    this.operationalLocationBodyShopId.set('');
    this.generalDataCache.set(null);
    this.steps.set([
      { id: 0, label: 'Dati Generali', routerLink: '/axa/professionals-general-data', icon: 'pi pi-user', completed: false, active: true, disabled: false },
      { id: 1, label: 'Dati Specifici', routerLink: '/axa/professionals-specific-data', icon: 'pi pi-building', completed: false, active: false, disabled: false },
      { id: 3, label: 'Collaboratori Professionisti e Impiegati Amministrativi', routerLink: '/axa/professionals-staff-collaborators', icon: 'pi pi-users', completed: false, active: false, disabled: false },
      { id: 2, label: 'Questionari e Allegati', routerLink: '/axa/professionals-questionnaire-attachments', icon: 'pi pi-file', completed: false, active: false, disabled: false },
    ]);
    sessionStorage.removeItem('professionals_wizard_state');
    console.log('🔄 [Wizard] State fully reset to default (NK contamination cleared)');
  }

  // Cache for general data to avoid duplicate API calls
  private generalDataCache = signal<any>(null);
  private generalDataLoadingMap = new Map<string, boolean>();
  
  // Public readonly access to cached general data
  readonly cachedGeneralData = this.generalDataCache.asReadonly();

  // Cache for specific data to avoid duplicate API calls
  private specificDataCache = signal<any>(null);
  private specificDataLoadingMap = new Map<string, boolean>();

  // Cache for questionnaire status to avoid duplicate API calls
  private questionnaireLoadingMap = new Map<string, boolean>();

  /**
   * RECUPERA LO STATO CORRENTE DAL BACKEND
   */
  loadCurrentStep(professionalId: string): void {
    this.http.get<RegistrationStepResponse>(`${environment.professionalsApiUrl}/${this.API_BASE_URL}/${professionalId}/registration-step/current`)
      .subscribe({
        next: (res) => this.syncStateFromBackend(res),
        error: (err) => console.error('Errore recupero step:', err)
      });
  }

  /**
   * Come loadCurrentStep ma ritorna l'Observable (senza subscribe interno).
   * Utile per concatenare operazioni che dipendono dallo step corrente.
   */
  getCurrentStepAsObservable(professionalId: string) {
    return this.http.get<RegistrationStepResponse>(
      `${environment.professionalsApiUrl}/${this.API_BASE_URL}/${professionalId}/registration-step/current`
    );
  }

  /**
   * AVANZA ALLO STEP SUCCESSIVO (POST /advance)
   */
  advanceToNextStep(professionalId: string): void {
    this.http.post<RegistrationStepResponse>(`${environment.professionalsApiUrl}/${this.API_BASE_URL}/${professionalId}/registration-step/advance`, {})
      .subscribe({
        next: (res) => this.syncStateFromBackend(res),
        error: (err) => console.error('Errore avanzamento step:', err)
      });
  }

  /**
   * COMPLETA LA REGISTRAZIONE (PUT /registration-step)
   * Marca la registrazione come completata
   */
  completeRegistration(professionalId: string, comments: string = 'Dati completati e verificati') {
    return this.http.put<RegistrationStepResponse>(
      `${environment.professionalsApiUrl}/${this.API_BASE_URL}/${professionalId}/registration-step`,
      {
        status: 'COMPLETED',
        comments: comments
      }
    );
  }

  /**
   * Steps di registrazione backend che indicano che il wizard (DATA_INPUT) è stato
   * interamente completato. In questi casi tutti gli step UI vanno marcati come completed.
   */
  private readonly POST_DATA_INPUT_STEPS = new Set<RegistrationStep>([
    RegistrationStep.VERIFICATION,
    RegistrationStep.MFA_SETUP,
    RegistrationStep.CONTRACT_SENT,
    RegistrationStep.CONTRACT_SIGNATURE,
    RegistrationStep.CONTRACT_COUNTERSIGNATURE,
  ]);

  /**
   * SINCRONIZZAZIONE CORE: Aggiorna i Signals dai dati del Backend
   */
  private syncStateFromBackend(data: RegistrationStepResponse): void {
    // Save the backend registration step and status
    this.currentRegistrationStep.set(data.step);
    this.currentRegistrationStatus.set(data.status);

    // ⚠️ NON aggiorniamo currentStep o l'active state degli step UI
    // Lo step attivo della UI è gestito dalla navigazione (setStep),
    // non dallo stato di registrazione del backend.
    //
    // ECCEZIONE: se la registrazione è avanzata oltre DATA_INPUT (VERIFICATION o successivi),
    // per definizione tutti gli step wizard erano già completati → li marchiamo tutti completed
    // così il wizard lo mostra correttamente senza dover ri-chiamare ogni endpoint.
    const allDone = this.POST_DATA_INPUT_STEPS.has(data.step as RegistrationStep);

    const updatedSteps = this.steps().map((step) => ({
      ...step,
      disabled: false, // Tutti gli step sempre abilitati per libera navigazione
      ...(allDone ? { completed: true } : {}),
    }));

    this.steps.set(updatedSteps);
    this.saveState(); // Sincronizza anche il SessionStorage
  }

  /**
   * Override locale del registration step (senza chiamata backend).
   * Usato quando il fiduciario vuole sostituire il documento firmato già caricato:
   * si resetta visivamente a CONTRACT_SIGNATURE per permettere un nuovo upload.
   * Alla prossima sync con il backend (advanceToNextStep) il valore verrà sovrascritto.
   */
  overrideRegistrationStep(step: RegistrationStep): void {
    this.currentRegistrationStep.set(step);
  }

  /**
   * NAVIGAZIONE E COMPATIBILITÀ UI
   */
  setStep(stepId: number): void {
    this.currentStep.set(stepId);
    const updatedSteps = this.steps().map((step) => ({
      ...step,
      active: step.id === stepId,
    }));
    this.steps.set(updatedSteps);
  }

  markStepCompleted(stepId: number, completed: boolean): void {
    const updatedSteps = this.steps().map((step) =>
      step.id === stepId ? { ...step, completed } : step
    );
    this.steps.set(updatedSteps);
    if (completed && stepId < this.steps().length - 1) {
      this.enableStep(stepId + 1);
    }
    this.saveState();
  }

  updateStepLabel(stepId: number, label: string): void {
    this.steps.update(steps => steps.map(step =>
      step.id === stepId ? { ...step, label } : step
    ));
    this.saveState();
  }

  enableStep(stepId: number, enabled: boolean = true): void {
    const updatedSteps = this.steps().map((step) =>
      step.id === stepId ? { ...step, disabled: !enabled } : step
    );
    this.steps.set(updatedSteps);
  }

  hideStep(stepId: number, hidden: boolean = true): void {
    const updatedSteps = this.steps().map((step) =>
      step.id === stepId ? { ...step, hidden } : step
    );
    this.steps.set(updatedSteps);
    this.saveState();
  }

  isStepEnabled(stepId: number): boolean {
    return !this.steps().find((s) => s.id === stepId)?.disabled;
  }

  /**
   * Returns the next visible (non-hidden) step after the given step id,
   * following the current ARRAY order (which defines the wizard sequence).
   * Used to navigate "forward" without hardcoding the next step id, so that
   * reordering the steps array is enough to change the flow.
   */
  getNextVisibleStep(currentStepId: number): ProfessionalsWizardStep | undefined {
    const steps = this.steps();
    const currentIndex = steps.findIndex((s) => s.id === currentStepId);
    if (currentIndex === -1) return undefined;
    return steps.slice(currentIndex + 1).find((s) => !s.hidden);
  }

  /**
   * GESTIONE SESSIONE
   *
   * Viene persistita nel sessionStorage la generalDataCache (tipo fiduciario)
   * e lo stato di completamento degli step, per sopravvivere ai reload di pagina.
   *
   * Active/hidden/routerLink NON vengono salvati: sono sempre derivati freschi
   * dalla navigazione e dai componenti.
   *
   * Lo stato NK è puramente in-memoria e non tocca mai il sessionStorage.
   */
  saveState(): void {
    if (this.isNetworkOnboarding()) return;
    const stepCompletions: Record<number, boolean> = {};
    this.steps().forEach(s => { stepCompletions[s.id] = s.completed; });
    sessionStorage.setItem('professionals_wizard_state', JSON.stringify({
      professionalId: sessionStorage.getItem('professional_id'),
      generalDataCache: this.generalDataCache(),
      stepCompletions,
    }));
  }

  loadState(professionalId?: string): void {
    // Ripristina generalDataCache e completamenti degli step.
    // Active/hidden vengono sempre derivati freschi dalla navigazione.
    const savedState = sessionStorage.getItem('professionals_wizard_state');
    if (savedState) {
      const state = JSON.parse(savedState);
      // Lo stato del wizard appartiene a un solo fiduciario. Gli stati salvati
      // prima dell'introduzione dell'ID non sono riutilizzabili in modo sicuro.
      if (professionalId && state.professionalId !== professionalId) {
        sessionStorage.removeItem('professionals_wizard_state');
        this.generalDataCache.set(null);
      } else {
        const cache = state.generalDataCache;
        if (cache !== undefined && cache !== null) {
          this.generalDataCache.set(cache);
        }
        const completions: Record<number, boolean> = state.stepCompletions ?? {};
        if (Object.keys(completions).length > 0) {
          this.steps.update(steps => steps.map(s =>
            completions[s.id] !== undefined ? { ...s, completed: completions[s.id] } : s
          ));
        }
      }
    }
    if (professionalId) {
      this.loadCurrentStep(professionalId);
    }
  }

  /**
   * PRECARICAMENTO DATI GENERALI
   * Carica i dati generali per determinare se lo step è completo o meno
   */
  /**
   * Check if general data is currently being loaded
   */
  isLoadingGeneralData(professionalId: string): boolean {
    return this.generalDataLoadingMap.get(professionalId) || false;
  }

  preloadGeneralDataStatus(professionalId: string, onComplete?: (tipologia: string | null) => void): void {
    // Se stiamo già caricando, non duplicare la chiamata
    if (this.generalDataLoadingMap.get(professionalId)) {
      console.log('⏳ General data already loading, skip duplicate call');
      return;
    }

    this.generalDataLoadingMap.set(professionalId, true);
    console.log(`📥 Preloading general data status for ${professionalId}`);

    this.professionalsService.getProfessionalGeneralData(professionalId)
      .pipe(
        catchError((error) => {
          console.log('ℹ️ No general data found yet (expected if not filled):', error);
          return of(null);
        })
      )
      .subscribe((response: any) => {
        this.generalDataLoadingMap.set(professionalId, false);

        // Defer signal updates to avoid NG0100 (ExpressionChangedAfterItHasBeenCheckedError)
        // when the HTTP response arrives during Angular's change detection cycle
        Promise.resolve().then(() => {
          if (response && response.data) {
            // Salva i dati in cache
            this.generalDataCache.set(response.data);

            // Se è una sede operativa, imposta la visibilità del wizard (solo Dati Specifici)
            // Questo garantisce il comportamento corretto anche al refresh della pagina
            if (response.data.parentProfessionalId && !this.isNetworkOnboarding()) {
              this.prepareOperationalLocationStepVisibility(professionalId);
            }

            // Controlla lo status e marca lo step come completato se è FILLED
            const status = response.data.generalDataSectionStatus;
            const isCompleted = status === 'FILLED';

            console.log(`📊 General data status: ${status}, marking step 0 as ${isCompleted ? 'completed' : 'not completed'}`);
            this.markStepCompleted(0, isCompleted);

            // Staff section (Impiegati Amministrativi e Collaboratori, id 3) completion
            // is driven by staffSectionStatus, now exposed on the general-data response.
            const staffStatus = response.data.staffSectionStatus;
            this.markStepCompleted(3, staffStatus === 'FILLED');

            onComplete?.(response.data.professionalType ?? null);
          } else {
            // Nessun dato trovato, step non completato
            console.log('ℹ️ No general data found, marking step 0 as not completed');
            this.generalDataCache.set(null);
            this.markStepCompleted(0, false);
            onComplete?.(null);
          }
        });
      });
  }

  /**
   * PRECARICAMENTO DATI SPECIFICI
   * Carica i dati specifici per determinare se lo step è completo o meno
   * Questo metodo dovrebbe essere chiamato dopo aver salvato i general data
   */
  preloadSpecificDataStatus(professionalId: string, tipologia: string): void {
    // Se stiamo già caricando, non duplicare la chiamata
    const cacheKey = `${professionalId}-${tipologia}`;
    if (this.specificDataLoadingMap.get(cacheKey)) {
      console.log('⏳ Specific data already loading, skip duplicate call');
      return;
    }

    this.specificDataLoadingMap.set(cacheKey, true);
    console.log(`📥 Preloading specific data status for ${tipologia}`);

    // Mappa la tipologia al metodo corretto del service
    let apiCall;
    switch (tipologia) {
      case 'DOCTOR':
        apiCall = this.professionalsService.getDoctorData(professionalId);
        break;
      case 'INVESTIGATOR':
        apiCall = this.professionalsService.getInvestigatorData(professionalId);
        break;
      case 'LAWYER':
        apiCall = this.professionalsService.getLawyerData(professionalId);
        break;
      case 'AUTO_EXPERT':
        apiCall = this.professionalsService.getAutoExpertData(professionalId);
        break;
      case 'PERITO_RE':
        apiCall = this.professionalsService.getREExpertData(professionalId);
        break;
      case 'BODYSHOP':
        apiCall = this.professionalsService.getBodyshopData(professionalId);
        break;
      case 'HAIL_PROVIDER':
        apiCall = this.professionalsService.getHailProviderData(professionalId);
        break;
      case 'NETWORK':
        // Network does not have a specific data section - hide and mark step as completed
        this.specificDataLoadingMap.set(cacheKey, false);
        Promise.resolve().then(() => {
          console.log('⏭️ NETWORK type: no specific data section, hiding step 1');
          this.hideStep(1);
          this.markStepCompleted(1, true);
        });
        return;
      default:
        console.warn(`⚠️ Unknown tipologia: ${tipologia}`);
        this.specificDataLoadingMap.set(cacheKey, false);
        return;
    }

    apiCall
      .pipe(
        catchError((error) => {
          console.log('ℹ️ No specific data found yet (expected if not filled):', error);
          return of(null);
        })
      )
      .subscribe((response: any) => {
        this.specificDataLoadingMap.set(cacheKey, false);

        if (response && response.data) {
          // Salva i dati in cache
          this.specificDataCache.set(response.data);
          console.log('✅ Specific data cached:', response.data);

          // Controlla lo status e marca lo step come completato se è FILLED
          const status = response.data.specificData?.specificDataSectionStatus;
          const isCompleted = status === 'FILLED';
          
          console.log(`📊 Specific data status: ${status}, marking step as ${isCompleted ? 'completed' : 'not completed'}`);
          this.markStepCompleted(1, isCompleted);
        } else {
          // Nessun dato trovato, step non completato
          console.log('ℹ️ No specific data found, marking step as not completed');
          this.specificDataCache.set(null);
          this.markStepCompleted(1, false);
        }
      });
  }

  /**
   * Recupera i dati specifici dalla cache
   * I form useranno questo metodo per evitare chiamate API duplicate
   */
  getCachedSpecificData(): any {
    return this.specificDataCache();
  }

  /**
   * Pulisce la cache dei dati specifici
   * Utile quando l'utente salva nuovi dati
   */
  clearSpecificDataCache(): void {
    this.specificDataCache.set(null);
    this.specificDataLoadingMap.clear();
  }

  /**
   * Aggiorna la cache con i nuovi dati salvati e aggiorna lo stato del wizard
   * @param data - I dati appena salvati (con specificDataSectionStatus)
   */
  updateSpecificDataCacheAfterSave(data: any): void {
    if (data) {
      this.specificDataCache.set(data);
      
      // Controlla lo status e aggiorna lo step
      const status = data.specificData?.specificDataSectionStatus;
      const isCompleted = status === 'FILLED';
      
      console.log(`📊 Cache updated after save with status: ${status}, marking step as ${isCompleted ? 'completed' : 'not completed'}`);
      this.markStepCompleted(1, isCompleted);
      this.saveState();
    }
  }

  /**
   * PRECARICAMENTO STATO QUESTIONARI
   * Carica lo stato dei questionari e allegati per determinare se lo step è completo
   */
  preloadQuestionnaireStatus(professionalId: string): void {
    // Se stiamo già caricando, non duplicare la chiamata
    if (this.questionnaireLoadingMap.get(professionalId)) {
      console.log('⏳ Questionnaire status already loading, skip duplicate call');
      return;
    }

    this.questionnaireLoadingMap.set(professionalId, true);
    console.log(`📥 Preloading questionnaire status for ${professionalId}`);

    // Usa gli stessi endpoint della pagina questionari, perché il riepilogo
    // questionnaire-step può restare PENDING mentre i singoli item sono già compilati.
    const cachedGeneralData = this.generalDataCache();
    const generalData$ = cachedGeneralData
      ? of(cachedGeneralData)
      : this.professionalsService.getProfessionalGeneralData(professionalId).pipe(
          map((response: any) => response?.data ?? response),
        );

    generalData$
      .pipe(
        catchError((error) => {
          console.error('❌ Error loading general data for questionnaire status:', error);
          return of(null);
        }),
      )
      .subscribe((data: any) => {
        const trusteeType = data?.professionalType;
        if (!trusteeType) {
          this.questionnaireLoadingMap.set(professionalId, false);
          Promise.resolve().then(() => this.markStepCompleted(2, false));
          return;
        }

        const vrfRequired = data?.tipologiaCarrozzeria !== 'AXA_DEUTSCHE_BANK';
        const vrfStatus$ = vrfRequired
          ? this.professionalsService.getVRFQuestionnaire(professionalId).pipe(catchError(() => of(null)))
          : of(null);

        forkJoin({
          questionnaires: this.questionnaireService
            .listQuestionnairesByProfessionalAndTrusteeType(professionalId, trusteeType)
            .pipe(catchError(() => of([]))),
          checklists: this.questionnaireService
            .getFileChecklistStatusByProfessionalAndTrusteeType(professionalId, trusteeType)
            .pipe(catchError(() => of([]))),
          vrf: vrfStatus$,
        }).subscribe({
          next: ({ questionnaires, checklists, vrf }) => {
            this.questionnaireLoadingMap.set(professionalId, false);

            const questionnairesCompleted = questionnaires.length === 0 ||
              questionnaires.every((item: any) => item.status === 'FILLED');
            const checklistsCompleted = checklists.length === 0 ||
              checklists.every((item: any) => item.isUploaded || !item.isMandatory);
            const vrfCompleted = !vrfRequired || vrf?.status === 'FILLED';
            const hasRequirements = vrfRequired || questionnaires.length > 0 || checklists.length > 0;
            const completed = hasRequirements && questionnairesCompleted && checklistsCompleted && vrfCompleted;

            console.log(`📋 Preloaded step 2 status from items: ${completed ? 'COMPLETED' : 'PENDING'}`);
            Promise.resolve().then(() => this.markStepCompleted(2, completed));
          },
          error: (error) => {
            this.questionnaireLoadingMap.set(professionalId, false);
            console.error('❌ Error loading questionnaire/checklist status:', error);
            Promise.resolve().then(() => this.markStepCompleted(2, false));
          },
        });
      });
  }

  /**
   * PRECARICAMENTO STATO ALLEGATI NK
   * Usato quando si carica la pagina "Dati Specifici" nel flusso NK (refresh o navigazione diretta):
   * verifica se tutti gli allegati obbligatori sono già stati caricati e marca lo step 2 di conseguenza.
   * Non usa getQuestionnaireStep (non applicabile agli ID NK-) né VRF.
   */
  preloadNKAttachmentsStatus(bodyShopId: string): void {
    console.log(`📥 [NK] Preloading attachments status for step 2 (bodyShopId: ${bodyShopId})`);
    this.questionnaireService
      .getFileChecklistStatusByProfessionalAndTrusteeType(bodyShopId, 'BODYSHOP')
      .pipe(catchError((err) => {
        console.error('❌ [NK] Error preloading attachments status:', err);
        return of([]);
      }))
      .subscribe((checklists: any[]) => {
        const allMandatoryUploaded = (checklists as any[]).every(
          (item: any) => item.isUploaded || !item.isMandatory
        );
        console.log(`📋 [NK] Step 2 attachments completed: ${allMandatoryUploaded}`);
        Promise.resolve().then(() => this.markStepCompleted(2, allMandatoryUploaded));
      });
  }

  reset(): void {
    sessionStorage.removeItem('professionals_wizard_state');
    location.reload();
  }
}
